import { createContext, useContext, useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "orthoped_consent";

const DEFAULTS = {
  necessary: true,   // always on
  analytics: false,
  marketing: false,
};

const ConsentContext = createContext();

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => readStored());
  const [bannerOpen, setBannerOpen] = useState(() => !readStored());

  // Persist to localStorage whenever consent changes
  useEffect(() => {
    if (consent) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    }
  }, [consent]);

  const acceptAll = useCallback(() => {
    const next = { necessary: true, analytics: true, marketing: true };
    setConsent(next);
    setBannerOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const next = { ...DEFAULTS };
    setConsent(next);
    setBannerOpen(false);
  }, []);

  const savePreferences = useCallback((prefs) => {
    const next = { necessary: true, ...prefs };
    setConsent(next);
    setBannerOpen(false);
  }, []);

  const openSettings = useCallback(() => {
    setBannerOpen(true);
  }, []);

  const hasConsent = useCallback(
    (category) => consent?.[category] ?? false,
    [consent],
  );

  return (
    <ConsentContext.Provider
      value={{
        consent,
        bannerOpen,
        acceptAll,
        rejectAll,
        savePreferences,
        openSettings,
        hasConsent,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};
